import React, { useState, useEffect } from 'react';
import {
  Button, TextField, Box, Typography, Alert, InputAdornment, IconButton, CircularProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function Login() {
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  // Автозаполнение из URL параметров
  useEffect(() => {
    const urlUsername = searchParams.get('username');
    const urlPassword = searchParams.get('password');
    if (urlUsername) setUsername(urlUsername);
    if (urlPassword) setPassword(urlPassword);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      return setError('Пожалуйста, заполните все поля');
    }

    try {
      setError('');
      setLoading(true);
      const result = await login(username, password);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Ошибка входа');
      }
    } catch (error) {
      setError('Не удалось выполнить вход: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="main" className="login-shell">
      <Box component="section" className="login-story">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box className="brand-mark"><GridViewRoundedIcon /></Box>
          <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: '-0.06em' }}>panel<Box component="span" sx={{ color: 'primary.main' }}>.</Box></Typography>
          <Typography variant="overline" color="text.secondary" sx={{ ml: 1 }}>DEV / 2.0</Typography>
        </Box>
        <Box className="login-story-copy">
          <Typography variant="overline" color="primary.main">Единое рабочее пространство</Typography>
          <Typography component="h2" className="login-headline">Всё дело.<br />В одной<br /><Box component="span" sx={{ color: 'primary.main' }}>панели.</Box></Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 330, mt: 3, fontSize: 15, lineHeight: 1.8 }}>Заказы, команда и аналитика.<br />Меньше переключений. Больше контроля.</Typography>
        </Box>
        <Box className="login-orbit" aria-hidden="true"><div /><div /><div /><span /></Box>
        <Box sx={{ display: 'flex', gap: 3, position: 'relative', zIndex: 1, pt: 4 }}>
          {['01 / Аналитика', '02 / Управление', '03 / Команда'].map(label => <Typography key={label} variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>{label}</Typography>)}
        </Box>
      </Box>
      <Box component="section" className="login-form-panel">
        <Typography variant="overline" color="text.secondary" sx={{ alignSelf: 'flex-end' }}>Панель управления</Typography>
        <Box sx={{ width: '100%', maxWidth: 380, my: 'auto', py: { xs: 5, md: 8 } }}>
          <Box sx={{ display: 'inline-flex', p: 1.25, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px', color: 'primary.main' }}><LockOutlinedIcon /></Box>
          <Typography component="h1" variant="h4" sx={{ mb: 1 }}>С возвращением</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>Войдите, чтобы продолжить работу.</Typography>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              required fullWidth id="username" label="Имя пользователя" name="username"
              autoComplete="username" autoFocus value={username}
              onChange={(e) => setUsername(e.target.value)} sx={{ mb: 2.5 }}
            />
            <TextField
              required fullWidth name="password" label="Пароль" type={showPassword ? 'text' : 'password'}
              id="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{ input: { endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'} aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)} edge="end">
                    {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ) } }}
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, height: 50 }} disabled={loading} endIcon={loading ? <CircularProgress size={17} color="inherit" /> : <ArrowForwardIcon />}>
              {loading ? 'Вход...' : 'Войти в панель'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>Нет доступа? Обратитесь к администратору.</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', color: 'text.secondary' }}>
          <Typography variant="caption">Только для сотрудников</Typography>
          <Typography variant="caption">Panel DEV</Typography>
        </Box>
      </Box>
    </Box>
  );
}
